import { useEffect } from 'react'
import { useRouter } from 'next/router'
import debounce from 'lodash/debounce'

import { useAuth } from '../../services/useAuth'
import useBuyer from './useBuyer'
import { isSuperUser, isSupplier } from '../../lib/supplier'
import useCustomGlobalState from './useCustomGlobalState'
import { isBuyerOnboardingSkipActive } from '../../utils/buyerOnboarding'

const ONBOARDING_MODAL_STATE_KEY = 'buyerOnboardingModalState'

const useBuyerOnboardingModal = () => {
  const [isOnboardingModalOpen, setOnboardingModalOpen] = useCustomGlobalState(
    ONBOARDING_MODAL_STATE_KEY,
    false,
  )

  const { user, isLoadingUser, isImpersonated } = useAuth()
  const { isBuyerOnboardingCompleted, isOnboardingDataLoading } = useBuyer()
  const { pathname } = useRouter()

  const openOnboardingModal = () => {
    setOnboardingModalOpen(true)
  }

  const closeOnboardingModal = () => {
    setOnboardingModalOpen(false)
  }

  // Auto-open buyer onboarding modal logic
  useEffect(() => {
    if (user) {
      if (isBuyerOnboardingSkipActive()) {
        return
      }

      const needsOnboarding = (
        !isBuyerOnboardingCompleted
        && !isSuperUser(user)
        && !isSupplier(user)
        && !isImpersonated
        && !pathname.includes('/logout')
        && !pathname.includes('/checkout')
        && !isOnboardingDataLoading
      )

      // Debounce opening onboarding modal to prevent race condition when fetching user data
      debounce(() => {
        if (needsOnboarding) {
          openOnboardingModal()
        }
      }, 500)()
    }
  }, [isLoadingUser, user, isImpersonated, pathname, isOnboardingDataLoading])

  const shouldShowOnboardingModal = isOnboardingModalOpen && !isOnboardingDataLoading

  return {
    shouldShowOnboardingModal,
    openOnboardingModal,
    closeOnboardingModal,
  }
}

export default useBuyerOnboardingModal
